"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { isExitComplete, isMounted, nextSilkOpen } from "./presentation";
import type { SheetTravelStatus } from "./Sheet";

export interface UseSheetPresentationOptions {
  /** Controlled open state. Omit for uncontrolled/trigger-driven use. */
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** Fires once after the exit animation completes (e.g. Inertia Modal's `afterLeave`). */
  onExitComplete?: () => void;
}

export interface SheetPresentation {
  isOpen: boolean;
  /** Whether the overlay subtree should render — true through the exit animation. */
  mounted: boolean;
  travelStatus: SheetTravelStatus;
  close: () => void;
  rootProps: { presented: boolean; onPresentedChange: (presented: boolean) => void };
  viewProps: {
    onTravelStatusChange: (status: SheetTravelStatus) => void;
    "data-travel-status": SheetTravelStatus;
  };
}

/**
 * Owns the presentation lifecycle of one Silk sheet: the controlled/uncontrolled
 * split, the queue that survives Silk's non-interruptible travel, the mount
 * latch that keeps the subtree alive through the exit animation, and a
 * fires-exactly-once `onExitComplete`.
 *
 * The transitions themselves live in `presentation.ts` (pure, unit-tested);
 * this is only their React binding.
 */
export function useSheetPresentation({
  isOpen,
  defaultOpen = false,
  onOpenChange,
  onExitComplete,
}: UseSheetPresentationOptions): SheetPresentation {
  // Uncontrolled state is owned here rather than delegated to Silk so that
  // `close()` and the mount latch read one consistent open state.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const desired = isOpen ?? uncontrolledOpen;

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (isOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isOpen, onOpenChange],
  );

  const [travelStatus, setTravelStatus] = useState<SheetTravelStatus>("idleOutside");
  const [silkOpen, setSilkOpen] = useState(false);

  useEffect(() => {
    const next = nextSilkOpen(silkOpen, desired, travelStatus);
    if (next !== silkOpen) setSilkOpen(next);
  }, [silkOpen, desired, travelStatus]);

  const settledOutside = isExitComplete(desired, travelStatus);

  // Release the subtree a paint after travel settles outside, rather than in
  // the same commit. Silk deactivates its layer in an after-paint effect and
  // returns focus to the opener from there; unmounting first forces that work
  // into React's cleanup, by which point the subtree is detached and Silk can
  // no longer tell focus was inside. Deferring to Silk's own 3s safe-to-unmount
  // window is not an option — many closed instances render at once on grid
  // screens, which is why the latch exists at all.
  const [released, setReleased] = useState(true);
  useEffect(() => {
    if (!settledOutside) {
      setReleased(false);
      return;
    }
    const frame = requestAnimationFrame(() => setReleased(true));
    return () => cancelAnimationFrame(frame);
  }, [settledOutside]);

  // Holds the callback while an exit is owed, so it fires exactly once — on
  // arrival at rest, or on teardown if the surface is unmounted still open.
  const owedExit = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    if (!settledOutside) {
      owedExit.current = onExitComplete;
      return;
    }
    const owed = owedExit.current;
    owedExit.current = undefined;
    owed?.();
  }, [settledOutside, onExitComplete]);
  useEffect(() => () => owedExit.current?.(), []);

  // Silk only knows the opener when the surface was presented through
  // `Sheet.Trigger`, which populates its `elementFocusedLastBeforeShowing`.
  // Every controlled call site opens by flipping state instead, and Silk's
  // `updateLayer` re-spreads that field on each update — so the element it
  // captured on the first add is overwritten with null, and dismissal drops
  // focus on <body>. Remember the opener here and restore it ourselves, but
  // only when the surface left focus nowhere, so Silk still wins when it can
  // do the job.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (desired && !wasOpen.current) {
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    }
    wasOpen.current = desired;
  }, [desired]);

  const mounted = isMounted(silkOpen, desired, travelStatus) || !released;
  useEffect(() => {
    if (mounted) return;
    const opener = openerRef.current;
    openerRef.current = null;
    if (!opener?.isConnected) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    opener.focus();
  }, [mounted]);

  const handleTravelStatusChange = useCallback((status: SheetTravelStatus) => setTravelStatus(status), []);
  const close = useCallback(() => handleOpenChange(false), [handleOpenChange]);

  return {
    close,
    isOpen: desired,
    mounted,
    rootProps: { onPresentedChange: handleOpenChange, presented: silkOpen },
    travelStatus,
    viewProps: { "data-travel-status": travelStatus, onTravelStatusChange: handleTravelStatusChange },
  };
}
